import { PAGES } from "@dune/types";
import type { SubmissionParticipantWithValidDecennialCertificate } from "@dune/types";
import { useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAddMaterialParticipantToReception } from "../../../../api/mutations/constructionSiteSubmission.mutations";
import { useGetConstructionSiteReceptionParticipants } from "../../../../api/queries/constructionSiteSubmission.queries";
import { MaterialToSubmissionForm } from "../../../../component/constructionSites/submission/participants/MaterialToSubmissionForm";
import PageLayoutWithTabs from "../../../../component/layout/PageLayoutWithTabs";
import { useMaterialToSubmissionForm } from "../../../../hooks/useParticipantToSubmissionForm";
import { ParticipantDocumentsReceptionTable } from "../../components/intervenants/ParticipantDocumentsReceptionTable";

const AddMaterialReceptionPage = () => {
	const navigate = useNavigate();
	const { constructionSiteId } = useParams<{ constructionSiteId: string }>();
	const [searchParams] = useSearchParams();
	const returnUrl = searchParams.get("returnUrl");

	const { data: receptionParticipants } =
		useGetConstructionSiteReceptionParticipants(Number(constructionSiteId));
	// Fees now come from reception participants

	const firstMaterial = useMemo(() => {
		const rp = receptionParticipants?.find((rp) => rp.type === "MATERIAL");
		if (!rp) return null;
		const isNewParticipant = !rp.isFromSubmission;
		const participantWithFlag = {
			...rp.participant,
			isNewForReception: isNewParticipant,
		};

		const obj = {
			id: rp.id,
			type: rp.type,
			designerType: rp.designerType ?? null,
			totalFeesHT: rp.totalFeesHT ?? 0,
			totalFeesTTC: rp.totalFeesTTC ?? 0,
			totalMissionsOrActivitiesOrMaterialTypes: [],
			participant: participantWithFlag,
			decennialCertificatesOfSubmissionOrValidFromOthers: null,
			insurer: null,
		} satisfies SubmissionParticipantWithValidDecennialCertificate;
		return obj;
	}, [receptionParticipants]);

	function goBack() {
		if (returnUrl) navigate(returnUrl);
		else if (constructionSiteId)
			navigate(PAGES.PROJECTS.reception.withParams(constructionSiteId, 3));
		else navigate(PAGES.PROJECTS.index);
	}

	const numericConstructionSiteId = constructionSiteId
		? Number(constructionSiteId)
		: 0;

	const addMaterial = useAddMaterialParticipantToReception(
		numericConstructionSiteId,
	);

	const formApi = useMaterialToSubmissionForm({
		mode: "add",
		onSubmit: (data) => addMaterial.mutate(data, { onSuccess: () => goBack() }),
	});

	if (!constructionSiteId) return null;

	return (
		<PageLayoutWithTabs title="Ajouter un matériau">
			<FormProvider {...formApi.form}>
				<form onSubmit={formApi.handleSubmit}>
					<MaterialToSubmissionForm
						constructionSiteId={numericConstructionSiteId}
						onSubmit={formApi.handleSubmit}
						onCancel={goBack}
						isSubmitting={addMaterial.isPending}
						documentsSlot={
							firstMaterial && (
								<ParticipantDocumentsReceptionTable
									submissionParticipant={firstMaterial}
									constructionSiteId={numericConstructionSiteId}
								/>
							)
						}
					/>
				</form>
			</FormProvider>
		</PageLayoutWithTabs>
	);
};

export default AddMaterialReceptionPage;
